import React, { useState, useContext, useRef, useCallback, useMemo, useEffect } from "react";
import { NavLink } from "react-router-dom";
import Moment from "react-moment";
import Switch from "react-switch";
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdDelete } from "react-icons/md";
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//pages & includes
import ManageSidebar from "../ManageSidebar";
import DataTable from "../../../../common/DataTable";

//functions
import { _t, getCookie } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import { FoodContext } from "../../../../../contexts/Food";

const FoodDepartmentList = () => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);

  //getting context values here
  let {
    //common
    loading,
  } = useContext(SettingsContext);

  let {
    //food dept
    foodDeptList,
    setFoodDeptList,
    foodDeptForSearch,
    setFoodDeptForSearch,
    setFoodDeptForStatus,
    getFoodDept,
    setPaginatedFoodDept,

    //pagination
    dataPaginating,
    setDataPaginating,
  } = useContext(FoodContext);

  useEffect(() => {
    if (getFoodDept) {
      getFoodDept();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // States hook here

  //search result
  let [searchedDeptTag, setSearchedDeptTag] = useState({
    list: null,
    searched: false,
  });

  //search food group here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedDeptTag({ ...searchedDeptTag, searched: false });
    } else {
      let searchedList = (foodDeptForSearch || []).filter((item) => {
        let lowerCaseItemName = item.name?.toLowerCase();
        let lowerCaseItemCode = item.code?.toLowerCase();
        return (
          (lowerCaseItemName && lowerCaseItemName.includes(searchInput)) ||
          (lowerCaseItemCode && lowerCaseItemCode.includes(searchInput))
        );
      });
      setSearchedDeptTag({
        ...searchedDeptTag,
        list: searchedList,
        searched: true,
      });
    }
  };

  const onClearSearch = () => {
    setSearchedDeptTag({ ...searchedDeptTag, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  const getListData = useCallback(() => {
    if (searchedDeptTag.searched) {
      return searchedDeptTag.list || [];
    }
    return foodDeptList?.data || [];
  }, [searchedDeptTag, foodDeptList]);

  const handleFetchData = (page) => {
    setPaginatedFoodDept(page);
  };

  //delete confirmation modal of food department
  const handleDeleteConfirmation = (item) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary rounded"
                onClick={() => {
                  handleDeleteFoodDept(item);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3 rounded" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  //delete food department
  const handleDeleteFoodDept = async (item) => {
    setDataPaginating(true);
    const deleteUrl = BASE_URL + `/settings/food-department/${item.id}`;
    try {
      const res = await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setFoodDeptList(res.data[0]);
          if (res.data[1]) setFoodDeptForSearch(res.data[1]);
          if (setFoodDeptForStatus && res.data[2] !== undefined) {
            setFoodDeptForStatus(res.data[2]);
          }
        } else {
          setFoodDeptList((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              data: (prev.data || []).filter((d) => d.id !== item.id),
              total: Math.max(0, (prev.total || 1) - 1),
            };
          });
          setFoodDeptForSearch((prev) =>
            (prev || []).filter((d) => d.id !== item.id)
          );
          if (setFoodDeptForStatus) {
            setFoodDeptForStatus((prev) =>
              (prev || []).filter((d) => d.id !== item.id)
            );
          }
        }
        if (searchedDeptTag.searched) {
          setSearchedDeptTag((prev) => ({
            ...prev,
            list: (prev.list || []).filter((d) => d.id !== item.id),
          }));
        }
        toast.success(`${_t(t("Food Department has been deleted successfully"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(`${_t(t(error.response.data.message))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      } else {
        toast.error(`${_t(t("Please try again"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
    } finally {
      setDataPaginating(false);
    }
  };

  const handleUpdateStatus = async (item) => {
    setDataPaginating(true);
    try {
      const nextStatus = Number(item.status) === 1 ? 0 : 1;
      const url = BASE_URL + `/settings/update-status-fooddepartment`;
      const formData = {
        id: Number(item.id),
        status: nextStatus,
      };
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setFoodDeptList(res.data[0]);
          if (res.data[1]) setFoodDeptForSearch(res.data[1]);
          if (setFoodDeptForStatus && res.data[2] !== undefined) {
            setFoodDeptForStatus(res.data[2]);
          }
        } else {
          setFoodDeptList((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              data: (prev.data || []).map((d) =>
                d.id === item.id ? { ...d, status: nextStatus } : d
              ),
            };
          });
          setFoodDeptForSearch((prev) =>
            (prev || []).map((d) =>
              d.id === item.id ? { ...d, status: nextStatus } : d
            )
          );
          if (setFoodDeptForStatus) {
            setFoodDeptForStatus((prev) =>
              (prev || []).map((d) =>
                d.id === item.id ? { ...d, status: nextStatus } : d
              )
            );
          }
        }
        if (searchedDeptTag.searched) {
          setSearchedDeptTag((prev) => ({
            ...prev,
            list: (prev.list || []).map((d) =>
              d.id === item.id ? { ...d, status: nextStatus } : d
            ),
          }));
        }
        toast.success(`${_t(t("Food Department has been updated."))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(`${_t(t(err.response.data.message))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      } else {
        toast.error(`${_t(t("Please try again"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      }
    } finally {
      setDataPaginating(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        label: _t(t("S/L")),
        render: (_item, index) =>
          searchedDeptTag.searched
            ? index + 1
            : index +
              1 +
              ((foodDeptList?.current_page || 1) - 1) *
                (foodDeptList?.per_page || 10),
      },
      {
        label: _t(t("Name")),
        key: "name",
      },
      {
        label: _t(t("Code")),
        key: "code",
      },
      {
        label: _t(t("Status")),
        render: (item) => (
          <Switch
            checked={Number(item?.status) === 1}
            name="status"
            onChange={() => {
              handleUpdateStatus(item);
            }}
            height={22}
            width={44}
            offColor="#ee5253"
          />
        ),
      },
      {
        label: _t(t("Action")),
        render: (item) => (
          <div className="d-flex justify-content-center">
            <NavLink
              to={`/dashboard/manage/food/department-edit/${item.id}`}
              className="btn btn-primary btn-sm rounded-sm"
              title="Edit"
            >
              <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
            </NavLink>
            <button
              className="btn btn-danger btn-sm ml-2 rounded-sm del-btn"
              title="Delete"
              onClick={() => handleDeleteConfirmation(item)}
            >
              <MdDelete className="del-icon" />
            </button>
          </div>
        ),
      },
      {
        label: _t(t("Created By")),
        render: (item) =>
          item?.created_by_name
            ? item.created_by_name
            : "-",
      },
      {
        label: _t(t("Created At")),
        render: (item) =>
          item?.created_at ? (
            <Moment format="LL">{item.created_at}</Moment>
          ) : (
            "-"
          ),
      },
      {
        label: _t(t("Updated By")),
        render: (item) =>
          item?.updated_by_name
            ? item.updated_by_name
            : "-",
      },
      {
        label: _t(t("Updated At")),
        render: (item) =>
          item?.updated_at ? (
            <Moment format="LL">{item.updated_at}</Moment>
          ) : (
            "-"
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, searchedDeptTag.searched, foodDeptList?.current_page, foodDeptList?.per_page]
  );

  return (
    <>
      <Helmet>
        <title>{_t(t("Food Department"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="t-pl-15 t-pr-15">
                  <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {!searchedDeptTag.searched
                              ? _t(t("Food Department List"))
                              : _t(t("Search Result"))}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6 col-lg-7">
                      <div className="row gx-3 align-items-center">
                        {/* Search group */}
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                ref={searchInputRef}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i
                                className="fa fa-search"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Add group modal trigger button */}
                        <div className="col-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/food/department-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {_t(t("add new"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  loading={loading || dataPaginating}
                  data={getListData()}
                  columns={columns}
                  emptyText="No data available"
                  pagination={{
                    isPageLoading: dataPaginating,
                    isPagination: true,
                    pageCount: foodDeptList?.last_page,
                    currentPage: foodDeptList?.current_page,
                    from: foodDeptList?.from,
                    to: foodDeptList?.to,
                    total: foodDeptList?.total,
                  }}
                  handleFetchData={handleFetchData}
                  searchPayload={{
                    isSearched: searchedDeptTag.searched,
                    onClearSearch,
                  }}
                />
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
    </>
  );
};

export default FoodDepartmentList;
